import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { Icon } from '../../../icon/icon';
import { TextBox } from '../../../text-box/text-box';
import { AddOnData } from '../add-ons';
import { ContentList } from './card';

type Props = {
  onChange: (data: AddOnData) => void;
  selected: boolean;
  comment?: string;
  selfReview: boolean;
};

const GcpAdjustmentContent = (props: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const onRadioChange = (selfReview: boolean) => {
    if (!props.selected) {
      return;
    }
    props.onChange({ gcpAdjustment: { selfReview } });
  };

  const onInputChanged = (value: string) => {
    props.onChange({ gcpAdjustment: { comment: value } });
  };

  return (
    <Component>
      <RadioGroup>
        <RadioOption onClick={(e) => e.stopPropagation()}>
          <input
            checked={!props.selfReview}
            type="radio"
            onChange={() => onRadioChange(false)}
            onClick={(e) => e.stopPropagation()}
          />
          <span>
            {t('setupDialog.addOns.gcpAdjustment.collaborationPartner', { ns: 'cloudProcessing' })}
          </span>
        </RadioOption>
        <RadioOption onClick={(e) => e.stopPropagation()}>
          <input
            checked={props.selfReview}
            type="radio"
            onChange={() => onRadioChange(true)}
            onClick={(e) => e.stopPropagation()}
          />
          <span>{t('setupDialog.addOns.gcpAdjustment.selfReview', { ns: 'cloudProcessing' })}</span>
        </RadioOption>
      </RadioGroup>

      {!props.selfReview ? (
        <>
          <ContentList>
            <li>
              <Icon
                color={theme.color.blue}
                fixedWidth={true}
                icon={['fal', 'circle-info']}
                style={{ marginTop: '0.2em' }}
              />
              <span>
                {t('setupDialog.addOns.gcpAdjustment.collaborationPartnerInfo', {
                  ns: 'cloudProcessing',
                })}
              </span>
            </li>
            <li>
              <Icon
                color={theme.color.green}
                fixedWidth={true}
                icon={['fal', 'check']}
                style={{ marginTop: '0.2em' }}
              />
              <span>
                {t('setupDialog.addOns.gcpAdjustment.collaborationPartnerChecklistGcp', {
                  ns: 'cloudProcessing',
                })}
              </span>
            </li>
            <li>
              <Icon
                color={theme.color.green}
                fixedWidth={true}
                icon={['fal', 'check']}
                style={{ marginTop: '0.2em' }}
              />
              <span>
                {t('setupDialog.addOns.gcpAdjustment.collaborationPartnerChecklistNotification', {
                  ns: 'cloudProcessing',
                })}
              </span>
            </li>
          </ContentList>

          <Comment>
            <span>{t('setupDialog.addOns.gcpAdjustment.comment', { ns: 'cloudProcessing' })}</span>
            <TextBox
              disabled={!props.selected}
              maxLength={255}
              value={props.comment ?? ''}
              onChange={(e) => onInputChanged(e.target.value)}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
          </Comment>
        </>
      ) : (
        <ContentList>
          <li>
            <Icon
              color={theme.color.blue}
              fixedWidth={true}
              icon={['fal', 'circle-info']}
              style={{ marginTop: '0.2em' }}
            />
            <span>
              {t('setupDialog.addOns.gcpAdjustment.selfReviewInfo', { ns: 'cloudProcessing' })}
            </span>
          </li>
          <li>
            <Icon
              color={theme.color.green}
              fixedWidth={true}
              icon={['fal', 'check']}
              style={{ marginTop: '0.2em' }}
            />
            <span>
              {t('setupDialog.addOns.gcpAdjustment.selfReviewChecklist', { ns: 'cloudProcessing' })}
            </span>
          </li>
          <li>
            <Icon
              color={theme.color.brand.dark}
              fixedWidth={true}
              icon={['fal', 'triangle-exclamation']}
              style={{ marginTop: '0.2em' }}
            />
            <span>
              {t('setupDialog.addOns.gcpAdjustment.selfReviewWarning', { ns: 'cloudProcessing' })}
            </span>
          </li>
          <li>
            <Icon
              color={theme.color.brand.dark}
              fixedWidth={true}
              icon={['fal', 'triangle-exclamation']}
              style={{ marginTop: '0.2em' }}
            />
            <span>
              {t('setupDialog.addOns.gcpAdjustment.selfReviewWarningMarkers', {
                ns: 'cloudProcessing',
              })}
            </span>
          </li>
        </ContentList>
      )}
    </Component>
  );
};

const Component = styled.section``;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4em;
  margin-bottom: 1em;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5em;
  cursor: pointer;
  font-size: 11pt;

  input {
    cursor: pointer;
  }
`;

const Comment = styled.label`
  span {
    display: block;
    font-size: 11pt;
    margin: 1em 0 0.1em 0;
  }
`;

export { GcpAdjustmentContent };
